import {
  type Variant,
  type Product,
  type Value,
  getFirstAttributeValue,
  getPrice,
  getVariantBySize,
} from '@scayle/storefront-nuxt'
import { ExistingItemHandling } from '@scayle/storefront-api'
import { Action } from '~/plugins/toast'

export async function usePromotionGiftSelection(
  gift: Product,
  promotedProduct?: Product,
) {
  const { $i18n, $alert } = useNuxtApp()
  const route = useRoute()
  const localePath = useLocalePath()
  const { openBasketFlyout } = useFlyouts()

  const activeVariant = useState<Variant | null | undefined>(
    `active-gift-variant-${gift.id}-${promotedProduct?.id ?? ''}`,
  )

  const isSelectionShown = useState(
    `gift-selection-${gift.id}-${promotedProduct?.id ?? ''}`,
    () => false,
  )

  const {
    brand,
    name: productName,
    variantWithLowestPrice,
  } = useProductBaseInfo(gift)

  const basket = useBasket()
  const basketActions = useBasketActions()
  const tracking = useTrackingEvents()

  const { fetching: basketIdle, addItem: addBasketItem } = basket
  const { showAddToBasketToast } = basketActions

  const { trackAddToBasket } = tracking

  const toggleGiftSelection = () => {
    isSelectionShown.value = !isSelectionShown.value
  }

  const isGiftSelectionShown = computed(() => isSelectionShown.value)

  const lowestPriorPrice = computed(
    () =>
      activeVariant.value?.lowestPriorPrice ||
      variantWithLowestPrice.value?.lowestPriorPrice ||
      gift?.lowestPriorPrice,
  )

  const price = computed(() =>
    activeVariant.value
      ? getPrice(activeVariant.value)
      : variantWithLowestPrice.value?.price,
  )

  const handleSelectedSize = (value: Value) => {
    if (gift.variants) {
      activeVariant.value = getVariantBySize(gift.variants, value, 'size')
    }
  }

  const size = computed(() => {
    return getFirstAttributeValue(activeVariant.value?.attributes, 'size')
      ?.value
  })

  const hasOneSizeVariantOnly = computed(() => {
    return hasOneSizeProductVariantOnly(gift)
  })

  const hasSpecial = computed(() => {
    return Boolean(
      !activeVariant.value && price.value?.appliedReductions.length,
    )
  })

  const images = computed(() => gift.images)

  const addItemToBasket = async (promotionId?: string) => {
    if (hasOneSizeVariantOnly.value && gift?.variants) {
      activeVariant.value = gift.variants[0]
    }

    if (!activeVariant.value) {
      $alert.show($i18n.t('basket.notification.select_size'), Action.confirm)
      return
    }

    const productName = brand.value || $i18n.t('wishlist.product')

    try {
      await addBasketItem({
        variantId: activeVariant.value.id,
        quantity: 1,
        existingItemHandling: ExistingItemHandling.ReplaceExisting,
        ...(promotionId && { promotionId }),
      })

      openBasketFlyout()

      if (route.path !== localePath(routeList.basket)) {
        showAddToBasketToast(true, gift)
      }

      if (gift) {
        trackAddToBasket(gift, 1, activeVariant.value)
      }
    } catch {
      $alert.show(
        $i18n.t('basket.notification.add_to_basket_error', { productName }),
        Action.confirm,
      )
    } finally {
      activeVariant.value = null
      toggleGiftSelection()
    }
  }

  return extendPromise(
    Promise.all([tracking, basket, basketActions]).then(() => ({})),
    {
      basketIdle,
      productName,
      brand,
      images,
      hasSpecial,
      addItemToBasket,
      lowestPriorPrice,
      handleSelectedSize,
      size,
      price,
      hasOneSizeVariantOnly,
      activeVariant,
      isGiftSelectionShown,
      toggleGiftSelection,
    },
  )
}
